//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import accessControlImage from '@/images/arch/AccessControl.png';
import DataMovementImage from '@/images/arch/DataMovement.png';
import productStackImage from '@/images/arch/ProductStack.png';
import systemArchitectureImage from '@/images/arch/SystemArch.svg';

type FileType = 'svg' | 'png' | 'jpg';

interface ArchitectureDiagram {
  id: string;
  title: string;
  description: string;
  imageUrl: string;   // Local path or remote URL.
  category?: string;  // Optional category for grouping.
}

export interface ArchitectureDiagramWithFileType extends ArchitectureDiagram {
  fileType: FileType; // Automatically inferred from imageUrl.
}

export interface ArchitectureCategory {
  id: string;
  name: string;
}

const inferFileType = (imageUrl: string): FileType => {
  const lowercaseUrl = imageUrl.toLowerCase();
  if (lowercaseUrl.endsWith('.svg') || lowercaseUrl.includes('.svg'))
    return 'svg';
  if (lowercaseUrl.endsWith('.png') || lowercaseUrl.includes('.png'))
    return 'png';
  if (lowercaseUrl.endsWith('.jpg') || lowercaseUrl.endsWith('.jpeg'))
    return 'jpg';

  // For webpack imported assets, check the URL structure.
  if (imageUrl.includes('svg'))
    return 'svg';

  // Default to png for raster images.
  return 'png';
};

const architectureDiagrams: ArchitectureDiagram[] = [
  {
    id: 'system-architecture',
    title: 'System Architecture',
    description:
      'High-level architecture diagram showing the interaction of system ' +
      'components',
    imageUrl: systemArchitectureImage,
    category: 'overview'
  },
  {
    id: 'product-stack',
    title: 'Product Stack',
    description:
      'Layered architecture diagram showing the full product stack from ' +
      'hardware through application, deployment modes, and enterprise ' +
      'capabilities',
    imageUrl: productStackImage,
    category: 'overview'
  },
  {
    id: 'data-movement',
    title: 'Data Movement',
    description: 'Data movement and workflow diagram',
    imageUrl: DataMovementImage,
    category: 'overview'
  },
  {
    id: 'access-control',
    title: 'Access Control',
    description: 'Access Control Mechanism',
    imageUrl: accessControlImage,
    category: 'features'
  },
];

// Get architecture diagrams filtered by category.  A null category returns all
// diagrams.
export const getArchitectureDiagrams = (
  category: string | null = null
): ArchitectureDiagramWithFileType[] => {
  let diagrams = architectureDiagrams;

  if (category && category !== 'all') {
    diagrams = architectureDiagrams.filter(
      diagram => diagram.category === category);
  }

  // Add inferred fileType to each diagram.
  return diagrams.map(diagram => ({
    ...diagram,
    fileType: inferFileType(diagram.imageUrl)
  }));
};

export const getDiagramById = (
  id: string
): ArchitectureDiagramWithFileType | null => {
  const diagram = architectureDiagrams.find(diagram => diagram.id === id);
  if (!diagram) return null;

  // Add inferred fileType.
  return { ...diagram, fileType: inferFileType(diagram.imageUrl) };
};

export const getArchitectureCategories = (): ArchitectureCategory[] => {
  const categories = new Set<string>();
  architectureDiagrams.forEach(diagram => {
    if (diagram.category)
      categories.add(diagram.category);
  });

  // Add an "all" category.
  const result: ArchitectureCategory[] = [{ id: 'all', name: 'All Diagrams' }];

  // Add other categories.
  Array.from(categories).forEach(category => {
    result.push({
      id: category,
      // Capitalize first letter.
      name: category.charAt(0).toUpperCase() + category.slice(1)
    });
  });

  return result;
};
